import classNames from 'classnames'
import React from 'react'
import { formatCurrency } from '../../helpers'
import {
  CartComboItem,
  OrderItemType,
} from '../../redux-toolkits/orders/orders.type'
import Icon from '../icon'

export const OrderItem = ({
  item,
  className = '',
  showImage = true,
  basePricePrefix = 'Base Price: ',
  showPriceBreakdown = true,
}: {
  item: OrderItemType
  className?: string
  showImage?: boolean
  basePricePrefix?: string
  showPriceBreakdown?: boolean
}) => {
  return (
    <div
      className={classNames(
        `flex flex-wrap gap-6 w-full max-w-[800px]
        py-6 first:!pt-0 last:!pb-0 only:!pt-0
        border-b last:border-b-0`,
        className,
      )}
    >
      {showImage && (
        <div className="min-w-fit">
          <img
            src={item.product.images[0] ?? ''}
            className="w-[140px] h-[140px] object-cover rounded-md"
          />
        </div>
      )}

      <div className="flex-1 flex flex-row gap-6 justify-between text-sec-black">
        <div className="flex-1 flex flex-col gap-1">
          <span className="text-black font-medium">{item.product.name}</span>

          {item.isGift && (
            <div className="mt-4 text-sm">
              <div className="text-black mt-4 mb-1 flex gap-2 items-center">
                <Icon id="gift-icon" width={18} height={18} /> This is a gift
              </div>

              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-4 justify-between">
                  <p>From:</p>
                  <p>{item.giftFrom}</p>
                </div>

                <div className="flex flex-col gap-1 justify-between">
                  <p>Gift note:</p>
                  <p className="text-left">{item.giftNote}</p>
                </div>
              </div>
            </div>
          )}

          {(item?.eventTicketTiers?.length ?? 0) > 0
            ? item.eventTicketTiers.map((ticketTier, idx) => {
                const key = `${idx}-variant`
                return (
                  <p key={key} className="text-sm text-sec-black">
                    x{ticketTier.quantity} {ticketTier.tierName}{' '}
                    {showPriceBreakdown ? (
                      <span>
                        ({formatCurrency(ticketTier.tierPrice ?? 0)}/ticket)
                      </span>
                    ) : undefined}
                  </p>
                )
              })
            : undefined}

          {(item?.variants?.length ?? 0) > 0 &&
            item.variants.map((variant, idx) => {
              const key = `${idx}-variant`
              return (
                <p key={key} className="text-sm text-sec-black">
                  {variant.variantId.name}: <span>{variant.value}</span>{' '}
                  {showPriceBreakdown ? (
                    <span>({formatCurrency(variant.price ?? 0)})</span>
                  ) : undefined}
                </p>
              )
            })}

          {(item?.comboItems?.length ?? 0) > 0 ? (
            <div className="flex flex-col gap-1">
              {Object.entries(
                item?.comboItems?.reduce?.(
                  (acc, item) => {
                    if (!acc[item.groupName]) {
                      acc[item.groupName] = []
                    }
                    acc[item.groupName].push(item)
                    return acc
                  },
                  {} as Record<string, CartComboItem[]>,
                ),
              )?.map(([name, items]) => {
                return (
                  <p key={name} className="text-sm text-sec-black group ">
                    <span className="">{name}:</span>{' '}
                    {items.map((item, idx) => {
                      return (
                        <React.Fragment key={`Extra-option-${idx}`}>
                          <span>
                            x{item.quantity} {item.name}
                            {showPriceBreakdown
                              ? ` (@${formatCurrency(item.price ?? 0)})`
                              : undefined}
                          </span>
                          <span className="last:hidden">, </span>
                        </React.Fragment>
                      )
                    })}
                  </p>
                )
              })}
            </div>
          ) : undefined}
        </div>

        <div className="flex text-base flex-col gap-0.5 text-black text-right">
          {showPriceBreakdown ? (
            <>
              <p className="">
                {`${basePricePrefix}${formatCurrency(item.basePricePerUnit)}`}
              </p>
              {item.markupAmountPerUnit > 0 && (
                <p className="">
                  {`Markup Price: ${formatCurrency(item.markupAmountPerUnit)}`}
                </p>
              )}
              {item.discountSnapshot && (
                <p className="">
                  {`Discount (Price Slash): ${formatCurrency(
                    item?.discountSnapshot?.discountAmount ?? 0,
                  )}`}
                </p>
              )}
            </>
          ) : (
            <>
              <p className="">
                {`${formatCurrency(item.totalPrice / item.quantity)}`}
              </p>
            </>
          )}

          <span>Qty: {item.quantity}</span>
        </div>
      </div>
    </div>
  )
}
